// Submits the /anfrage lead form to the anfrage-lead-relay Lambda (AWS,
// eu-west-1), which forwards server-to-server to Zoho Flow and creates the
// Lead in Zoho CRM on its own side. Not called directly from here: Zoho
// Flow's webhook trigger has no CORS support (confirmed against three
// separate flow URLs — no Access-Control-Allow-Origin on its OPTIONS
// preflight), so a direct browser fetch always fails before it's even sent.
// The relay exists purely to fix that; no CRM credentials live in this repo.
// See .env.example for the relay URL var.

import { getAttributionParams } from "./tracking-url";
import type { Lang } from "../i18n";

export interface LeadFormPayload {
  name: string;
  email: string;
  company: string;
  intent: string;
  regions: string[];
  message: string;
  lang: Lang;
}

export class LeadFormSubmitError extends Error {}

/**
 * POSTs the lead to the relay. Throws on any failure (missing config,
 * network error, non-2xx) — callers must show this, never swallow it, so a
 * lost submission is never silent.
 */
export async function submitLeadForm(payload: LeadFormPayload): Promise<void> {
  const relayUrl = import.meta.env.VITE_LEAD_RELAY_URL as string | undefined;
  if (!relayUrl) {
    throw new LeadFormSubmitError("Lead form is not configured (missing relay URL).");
  }

  const body = {
    ...payload,
    ...getAttributionParams(),
    page_url: typeof window !== "undefined" ? window.location.href : "",
    submitted_at: new Date().toISOString(),
  };

  let res: Response;
  try {
    res = await fetch(relayUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new LeadFormSubmitError(
      e instanceof Error ? e.message : "Network error while submitting the form."
    );
  }

  if (!res.ok) {
    throw new LeadFormSubmitError(`Lead relay responded with ${res.status}`);
  }
}
